import {
	type ChangeEvent,
	type KeyboardEvent,
	type MouseEvent,
	type PropsWithChildren,
	useCallback,
	useEffect,
	useState,
} from 'react';

import { type FieldData } from './DeviceIntelligenceProvider';

import { useDeviceIntelligence } from '../hooks/useDeviceIntelligence';

export function DeviceBehaviorField(props: PropsWithChildren) {
	const [fieldData, setFieldData] = useState<FieldData>({});
	const { reportFieldData } = useDeviceIntelligence();

	useEffect(() => {
		reportFieldData(fieldData);
	}, [fieldData, reportFieldData]);

	const updateFieldNameOnly = useCallback(
		(target: HTMLInputElement) => {
			const name = target.name;
			setFieldData(prevData => ({ ...prevData, name }));
		},
		[setFieldData],
	);

	const updateFieldData = useCallback(
		(target: HTMLInputElement) => {
			const name = target.name;
			const currentTime = performance.now();
			
			setFieldData(prevData => {
				const currentKeystrokeCount = (prevData.keystroke_count || 0) + 1;
				const firstKeystrokeTime = prevData.first_keystroke_time || currentTime;
				
				let typingSpeedWpm: number | undefined;
				if (currentKeystrokeCount >= 10) {
					const durationMinutes = (currentTime - firstKeystrokeTime) / (1000 * 60);
					if (durationMinutes > 0) {
						// Standard conversion: 5 keystrokes = 1 word
						typingSpeedWpm = (currentKeystrokeCount / 5) / durationMinutes;
					}
				}
				
				return {
					...prevData,
					name,
					keystroke_count: currentKeystrokeCount,
					typing_speed_wpm: typingSpeedWpm,
					first_keystroke_time: firstKeystrokeTime,
					last_keystroke_time: currentTime,
				};
			});
		},
		[setFieldData],
	);

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		const target = e.target as HTMLInputElement;
		updateFieldData(target);
	};

	const onClick = (e: MouseEvent<HTMLInputElement>) => {
		const target = e.target as HTMLInputElement;
		updateFieldNameOnly(target);
	};

	const onChange = (e: ChangeEvent<HTMLInputElement>) => {
		const target = e.target as HTMLInputElement;
		updateFieldNameOnly(target);
	};

	return (
		<div role="presentation" onClick={onClick} onChange={onChange} onKeyDown={onKeyDown}>
			{props.children}
		</div>
	);
}
